import { computed, ref, watch } from 'vue'
import { groupPricingOptions } from '@/utils/priceModels.js'
import PricingApi from '@/api/pricing.js'

export function useInstancePrices () {
  const premiumOptions = ref([])
  const prices = ref([])
  const locale = ref(null)
  const monthlyUserPrice = ref(null)

  const pricingOptions = computed(() => groupPricingOptions(prices.value))

  /**
   * Picks the id of the model for monthly transactions.
   *
   * @returns {string}
   */
  const monthlyModel = computed(() => {
    return prices.value
      .find(({ term_model: model }) => model.payment_obligation_month === 1 && model.payment_period_month === 1)
      ?.term_model
      ?.term_model_id
  })

  /**
   * Minimum amount of users to book based on the price-definitions.
   *
   * @returns {number}
   */
  const userMinimum = computed(() => prices.value.reduce((minimum, { min_users: min }) =>
    min < minimum || !minimum ? min : minimum, null
  ))

  /**
   * Maximum amount of users to book based on the price-definitions.
   *
   * @param {object} state
   * @returns {number}
   */
  const userMaximum = computed(() => prices.value.reduce((maximum, { max_users: max }) =>
    max > maximum || !maximum ? max : maximum, null
  ))

  /**
   * @param {string} countryCode
   * @returns {Promise}
   */
  async function loadPricesForCountry (countryCode) {
    const res = await PricingApi.getPricingModel(countryCode)
    prices.value = await res.json()
  }

  /**
   * @param {string} countryCode
   * @returns {Promise}
   */
  async function loadPremiumOptionsForCountry (countryCode) {
    const res = await PricingApi.getPremiumOptions(countryCode)
    const allOptions = await res.json()
    premiumOptions.value = allOptions.filter(option => !option.is_monthly_user)
    monthlyUserPrice.value = allOptions.find(option => option.is_monthly_user)?.price
  }

  watch(locale, to => {
    const countryCode = to.locale.split('_')[1]

    if (countryCode) {
      loadPricesForCountry(countryCode)
      loadPremiumOptionsForCountry(countryCode)
    }
  })

  return {
    premiumOptions,
    pricingOptions,
    monthlyModel,
    monthlyUserPrice,
    userMinimum,
    userMaximum,
    locale,

    loadPricesForCountry,
    loadPremiumOptionsForCountry,
  }
}
